import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import moment from 'moment-timezone';

import { AmcReachAndFrequency } from '../types/AmcReachAndFrequency';
import { AttributionCriteria } from '../types/Criteria/AttributionCriteria';
import { PerformanceMetric } from '../types/CampaignPerformance';
import ApiService, { BYPASS_WAIT_INDICATOR } from '../ApiService';

interface AttributionPolkMatchbackDateRanges {
    startDate: Date;
    endDate: Date;
    previousStartDate: Date;
    previousEndDate: Date;
}

interface UseAttributionPolkMatchbackQueries {
    currentPerformanceQuery: UseQueryResult<PerformanceMetric[]>;
    previousPerformanceQuery: UseQueryResult<PerformanceMetric[]>;

    currentReachFrequencyQuery: UseQueryResult<AmcReachAndFrequency[]>;
    previousReachFrequencyQuery: UseQueryResult<AmcReachAndFrequency[]>;
}

const useAttributionPolkMatchbackQueries = (
    attributionCriteria: AttributionCriteria,
    attributionDate: Date | null | undefined
): UseAttributionPolkMatchbackQueries => {
    const dateRanges = useMemo((): AttributionPolkMatchbackDateRanges | null => {
        if (!attributionDate) return null;

        const startDate = moment(attributionDate).startOf('month').toDate();
        const endDate = moment(attributionDate).endOf('month').toDate();

        const previousStartDate = moment(attributionDate).subtract(1, 'month').startOf('month').toDate();

        const previousEndDate = moment(attributionDate).subtract(1, 'month').endOf('month').toDate();

        return { startDate, endDate, previousStartDate, previousEndDate };
    }, [attributionDate]);

    const enabled =
        !!dateRanges &&
        // uncomment if you only want to fetch when advertisers exist:
        // attributionCriteria.advertiserIds?.length > 0 &&
        true;

    const baseQueryKey = useMemo(
        () =>
            [
                'ATTRIBUTION',
                'POLK_MATCHBACK',
                {
                    agencyIds: attributionCriteria.agencyIds,
                    dealerIds: attributionCriteria.advertiserIds,
                    oemIds: attributionCriteria.oemIds,
                    oemProgramIds: attributionCriteria.oemProgramIds,
                    agencyMarketRegions: attributionCriteria.agencyMarketRegions,
                    dataSet: attributionCriteria?.dataSet ?? null,
                },
            ] as const,
        [attributionCriteria]
    );

    const currentPerformanceQuery = useQuery({
        queryKey: [
            ...baseQueryKey,
            'CAMPAIGN_PERFORMANCE',
            'CURRENT',
            dateRanges?.startDate?.toISOString() as string,
            dateRanges?.endDate?.toISOString() as string,
        ],
        queryFn: async ({ signal }) => {
            if (!dateRanges) {
                throw new Error('Missing date ranges');
            }

            const { data = [] } = await ApiService.getCampaignPerformanceList(
                {
                    agencyIds: attributionCriteria.agencyIds,
                    dealerIds: attributionCriteria.advertiserIds,
                    oemIds: attributionCriteria.oemIds,
                    oemProgramIds: attributionCriteria.oemProgramIds,
                    agencyMarketRegions: attributionCriteria.agencyMarketRegions,
                    startDate: dateRanges.startDate,
                    endDate: dateRanges.endDate,
                    dataSet: attributionCriteria?.dataSet ?? null,
                },
                { signal, ...BYPASS_WAIT_INDICATOR }
            );

            return data;
        },
        refetchOnWindowFocus: false,
        enabled,
    });

    const previousPerformanceQuery = useQuery({
        queryKey: [
            ...baseQueryKey,
            'CAMPAIGN_PERFORMANCE',
            'PREVIOUS',
            dateRanges?.previousStartDate?.toISOString() as string,
            dateRanges?.previousEndDate?.toISOString() as string,
        ],
        queryFn: async ({ signal }) => {
            if (!dateRanges) {
                throw new Error('Missing date ranges');
            }

            const { data = [] } = await ApiService.getCampaignPerformanceList(
                {
                    agencyIds: attributionCriteria.agencyIds,
                    dealerIds: attributionCriteria.advertiserIds,
                    oemIds: attributionCriteria.oemIds,
                    oemProgramIds: attributionCriteria.oemProgramIds,
                    agencyMarketRegions: attributionCriteria.agencyMarketRegions,
                    startDate: dateRanges.previousStartDate,
                    endDate: dateRanges.previousEndDate,
                    dataSet: attributionCriteria?.dataSet ?? null,
                },
                { signal, ...BYPASS_WAIT_INDICATOR }
            );

            return data;
        },
        refetchOnWindowFocus: false,
        enabled,
    });

    const currentReachFrequencyQuery = useQuery({
        queryKey: [
            ...baseQueryKey,
            'AMC_REACH_FREQUENCY',
            'CURRENT',
            dateRanges?.startDate?.toISOString() as string,
            dateRanges?.endDate?.toISOString() as string,
        ],
        queryFn: async ({ signal }) => {
            if (!dateRanges) {
                throw new Error('Missing date ranges');
            }

            const { data = [] } = await ApiService.getAmcReachAndFrequencyList(
                {
                    dealerIds: attributionCriteria.advertiserIds,
                    agencyIds: attributionCriteria.agencyIds,
                    oemIds: attributionCriteria.oemIds,
                    oemProgramIds: attributionCriteria.oemProgramIds,
                    agencyMarketRegions: attributionCriteria.agencyMarketRegions,
                    campaignName: 'Global',
                    startDate: dateRanges.startDate,
                    endDate: dateRanges.endDate,
                },
                { signal, ...BYPASS_WAIT_INDICATOR }
            );

            return data;
        },
        refetchOnWindowFocus: false,
        enabled,
    });

    const previousReachFrequencyQuery = useQuery({
        queryKey: [
            ...baseQueryKey,
            'AMC_REACH_FREQUENCY',
            'PREVIOUS',
            dateRanges?.previousStartDate?.toISOString() as string,
            dateRanges?.previousEndDate?.toISOString() as string,
        ],
        queryFn: async ({ signal }) => {
            if (!dateRanges) {
                throw new Error('Missing date ranges');
            }

            const { data = [] } = await ApiService.getAmcReachAndFrequencyList(
                {
                    dealerIds: attributionCriteria.advertiserIds,
                    agencyIds: attributionCriteria.agencyIds,
                    oemIds: attributionCriteria.oemIds,
                    oemProgramIds: attributionCriteria.oemProgramIds,
                    agencyMarketRegions: attributionCriteria.agencyMarketRegions,
                    campaignName: 'Global',
                    startDate: dateRanges.previousStartDate,
                    endDate: dateRanges.previousEndDate,
                },
                { signal, ...BYPASS_WAIT_INDICATOR }
            );

            return data;
        },
        refetchOnWindowFocus: false,
        enabled,
    });

    return {
        currentPerformanceQuery,
        previousPerformanceQuery,

        currentReachFrequencyQuery,
        previousReachFrequencyQuery,
    };
};

export { useAttributionPolkMatchbackQueries };
